<template>
  <div class="month-tab">
    <div v-if="isFirstLoading" class="step-dynamic__loader">
      <Loader size="24" />
    </div>
    <div v-else>
      <!-- Слайдер -->
      <div class="swiper month-swiper">
        <div class="swiper-wrapper">
          <div
            class="swiper-slide"
            v-for="(month, idx) in months"
            :key="idx"
          >
            <!-- month header -->
            <div class="month-header">
              <Button
                class="swiper-button-prev month__button"
                shape="circle"
                size="lg"
                variant="clear"
                iconLeft="left"
                :disabled="isPrevDisabled"
              />
              <div v-if="month.label" class="month-title">
                {{ month.label }} • <span>{{ month.stats.steps }} {{ getStepWord(month.stats.steps) }}</span>
              </div>
              <Button
                class="swiper-button-next month__button"
                shape="circle"
                size="lg"
                variant="clear"
                iconLeft="right"
                :disabled="isNextDisabled"
              />
            </div>
            <!-- month grid -->
            <div class="month-grid">
              <ProgressBarSteps
                v-for="(day, index) in month.days"
                :key="index"
                :date="day.date"
                :value="day.stepsCount || 0"
                :targetSteps="day.targetCount || 0"
                :check="false"
                :size="40"
                :rotate="0"
                month
                :month-day="numberOfDaysInMonth(day.date)"
                :class="{ outside: !day.inCurrentMonth }"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="step-dynamic__items">
      <div class="step-dynamic__item">
        <Icon name="walking" size="xl" />
        <div>
          <span class="step-dynamic__item-value">{{ months[slideIndex].stats.kilometers || 0 }}</span>
          <span class="step-dynamic__item-unit">км</span>
        </div>
      </div>
      <div class="step-dynamic__item step-dynamic__item_position" @click="$emit('goRating')">
        <Icon name="trophy" size="xl" />
        <div>
          <span class="step-dynamic__item-unit">№</span>
          <span class="step-dynamic__item-value">{{ months[slideIndex].stats.position || 0 }}</span>
        </div>
      </div>
      <div class="step-dynamic__item">
        <img :src="coinImg" />
        <span class="step-dynamic__item-value">{{ months[slideIndex].stats.points || 0 }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper';
import dayjs from 'dayjs';
import { mapGetters, mapActions } from 'vuex';
import CoinImg from '@/assets/img/coin.svg';
import ProgressBarSteps from '../ProgressBarCircle/ProgressBarSteps.vue';

export default {
  name: 'MonthTab',
  components: {
    ProgressBarSteps,
  },
  data() {
    return {
      baseMonth: dayjs().startOf('month'),
      months: [
        {
          loading: true, days: [], stats: {}, label: '',
        }, // prev
        {
          loading: true, days: [], stats: {}, label: '',
        }, // current
        {
          loading: true, days: [], stats: {}, label: '',
        }, // next
      ],
      coinImg: CoinImg,
      isFirstLoading: true,
      slideIndex: 1,
      swiper: null,
    };
  },
  watch: {
    isPrevDisabled: {
      immediate: true,
      handler(newVal) {
        if (this.swiper) {
          this.swiper.allowSlidePrev = !newVal;
        }
      },
    },
    isNextDisabled: {
      immediate: true,
      handler(newVal) {
        if (this.swiper) {
          this.swiper.allowSlideNext = !newVal;
        }
      },
    },
  },
  computed: {
    ...mapGetters('steps', ['stepsHistory', 'participantPositionData']),
    ...mapGetters('user', ['profile']),
    isNextDisabled() {
      return this.isCurrentMonthSelected();
    },
    isPrevDisabled() {
      return this.isLeftDisabled();
    },
  },
  methods: {
    ...mapActions('steps', ['getStepsHistory', 'getParticipantPositionData']),
    numberOfDaysInMonth(date) {
      return dayjs(date).date();
    },
    getStepWord(count) {
      const mod10 = count % 10;
      const mod100 = count % 100;

      if (mod10 === 1 && mod100 !== 11) return 'шаг';
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'шага';
      return 'шагов';
    },
    parseMonthLabel(label) {
      const monthNames = [
        'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
        'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
      ];
      // label типа "май 2025"
      if (!label) return null;
      const parts = label.split(' ');
      if (parts.length !== 2) return null;
      const monthStr = parts[0].toLowerCase();
      const year = Number(parts[1]);
      const month = monthNames.indexOf(monthStr);
      // eslint-disable-next-line no-restricted-globals
      if (month === -1 || isNaN(year)) return null;
      return { month, year };
    },
    isCurrentMonthSelected() {
      const now = new Date();
      const selectedMonth = this.months[this.slideIndex];
      if (!selectedMonth || !selectedMonth.label) return false;
      const parsed = this.parseMonthLabel(selectedMonth.label);
      if (!parsed) return false;
      return (
        parsed.year === now.getFullYear()
        && parsed.month === now.getMonth()
      );
    },
    isLeftDisabled() {
      if (!this.profile?.createdOn) return false;

      const createdDate = new Date(this.profile.createdOn);

      const selectedMonth = this.months[this.slideIndex];
      if (!selectedMonth || !selectedMonth.label) return false;

      const parsed = this.parseMonthLabel(selectedMonth.label);

      if (!parsed) return false;

      return (
        parsed.year === createdDate.getFullYear()
        && parsed.month === createdDate.getMonth()
      );
    },
    async loadMonthData(monthDate) {
      const startOfMonth = monthDate.startOf('month');
      const startOfCalendar = startOfMonth.subtract((startOfMonth.day() + 6) % 7, 'day');
      const days = [];
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < 36; i++) {
        const date = startOfCalendar.add(i, 'day');
        days.push({
          date: date.format('YYYY-MM-DD'),
          inCurrentMonth: date.month() === startOfMonth.month(),
          stepsCount: 0,
          targetCount: 0,
        });
      }
      // Загрузка истории шагов
      await this.getStepsHistory({
        dateFrom: days[0].date,
        dateTo: days[35].date,
        periodType: 'Daily',
      });
      const monthKey = monthDate.format('YYYY-MM-DD');
      // Загрузка статистики
      await this.getParticipantPositionData({ dateFrom: monthKey, periodType: 'Monthly' });
      const stepsMap = this.stepsHistory.reduce((acc, item) => {
        acc[item.date] = item;
        return acc;
      }, {});
      const daysWithData = days.map((day) => ({
        ...day,
        stepsCount: stepsMap[day.date]?.stepsCount || 0,
        targetCount: stepsMap[day.date]?.targetCount || 6000,
      }));
      const stats = {
        kilometers: this.participantPositionData.kilometers || 0,
        position: this.participantPositionData.position || '-',
        points: this.participantPositionData.accruedPoints || 0,
        steps: this.participantPositionData.steps || 0,
      };
      return {
        loading: false,
        days: daysWithData,
        stats,
        label: monthDate.format('MMMM YYYY'),
      };
    },
    async initThreeMonths() {
      this.isFirstLoading = true;
      const prev = await this.loadMonthData(this.baseMonth.subtract(1, 'month'));
      const curr = await this.loadMonthData(this.baseMonth);
      const next = await this.loadMonthData(this.baseMonth.add(1, 'month'));
      this.months = [prev, curr, next];
      this.isFirstLoading = false;
    },
    async onSlideChange() {
      if (!this.swiper) return;
      const newIndex = this.swiper.activeIndex;
      if (newIndex === this.slideIndex) return;
      if (newIndex > this.slideIndex) {
        // Вперёд
        this.baseMonth = this.baseMonth.add(1, 'month');
        this.months.shift();
        this.months.push({
          loading: true, days: [], stats: {}, label: '',
        });
        const newNext = await this.loadMonthData(this.baseMonth.add(1, 'month'));
        this.$set(this.months, 2, newNext);
      } else if (newIndex < this.slideIndex) {
        // Назад
        this.baseMonth = this.baseMonth.subtract(1, 'month');
        this.months.pop();
        this.months.unshift({
          loading: true, days: [], stats: {}, label: '',
        });
        const newPrev = await this.loadMonthData(this.baseMonth.subtract(1, 'month'));
        this.$set(this.months, 0, newPrev);
      }
      this.$nextTick(() => {
        this.swiper.slideTo(1, 0, false);
        this.slideIndex = 1;
      });
    },
  },
  async mounted() {
    await this.initThreeMonths();
    this.$nextTick(() => {
      this.swiper = new Swiper('.month-swiper', {
        slidesPerView: 1,
        initialSlide: 1,
        spaceBetween: 20,
        allowTouchMove: true,
        centeredSlides: false,
        navigation: {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        },
        on: {
          slideChange: this.onSlideChange,
        },
      });
      this.swiper.allowSlidePrev = !this.isPrevDisabled;
      this.swiper.allowSlideNext = !this.isNextDisabled;
    });
  },
};
</script>

<style scoped lang="scss">
@import './StepDynamicModal';

.month-swiper {
  overflow: hidden;
}

.month-title {
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 5%;
  text-transform: uppercase;
  color: $color-black-45;
  width: 100%;
  span {
    color: $color-black-85;
  }
  @media(max-width: 998px) {
    font-size: 12px;
  }
}

.month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  justify-items: center;
  gap: 10px;
  margin-bottom: 50px;
  @media (max-width: 500px) {
    margin: 0 auto 50px;
    max-width: 312px;
    row-gap: 10px;      /* между строками */
    column-gap: 2px;
    // grid-template-columns: repeat(3, 1fr);
  }
}

.month-header {
  margin: 20px -16px;
  padding: 0 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;
  @media (max-width: 998px) {
    justify-content: center;
    height: 50px;
  }
  .month__button {
    position: relative;
    top: 50%;
    transform: translateY(-50%);
    width: 48px;
    height: 48px;
    background: $color-white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 1px rgba(0, 0, 0, 0.08);
    border-radius: 50% !important;
    outline: none;
    line-height: 1;
    z-index: 2;
    transition: 0.2s ease-in-out;
    color: $color-black-85;
    &.disabled {
      opacity: 0;
    }
    @media (max-width: 998px) {
      display: none;
    }
    svg {
      width: 24px;
      height: 24px;
      @media (min-width: breakpoint(md)) {
        width: 30px;
        height: 30px;
      }
    }
  }
}

.step-dynamic__loader {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 442px;
}
</style>
